import { appReleaseFactsSchema, appSourceSchema } from "./app-source.js";
import { pagedEnvelopeSchema, pagedQueryFields } from "./paging.js";
import { ringSchema } from "./rings.js";
import { z } from "zod";
//#region src/app-ring-versions.ts
/**
* `GET /v1/apps/ring-versions`: every app the caller can read, with its source record and, per
* public ring, the pinned version and the build facts of that version (plan
* 2026-10-07-app-source-record, C-3, D16).
*
* The route lives in the release-engine apps controller, declared before `@Get(':id')`. It has
* the gate of `GET /v1/apps`: `@FilterByResourcePermission('cp:app:read', { wildcardPermissions:
* ['cp:app:list-all'] })`. So a caller sees the apps `GET /v1/apps` shows it, and no other.
*
* Consumers: `CpClient.listAppRingVersions` in `@zion/sdk-node` (for `zion app list --sdk`, D17),
* and the MCP read tool `list_app_ring_versions` in site-admin-api (D20).
*/
/** The path of the route, relative to the CP base URL. */
const APP_RING_VERSIONS_PATH = "/v1/apps/ring-versions";
/** The largest page the route serves: the bound of `GET /v1/apps` (defaults table, sibling row). */
const APP_RING_VERSIONS_MAX_LIMIT = 200;
/**
* The page size when a request carries no `limit`. The service applies it; the query schema
* carries no `.default()` (paging.ts, R-26).
*/
const APP_RING_VERSIONS_DEFAULT_LIMIT = 100;
/**
* The query string of `GET /v1/apps/ring-versions`: the house paging fragment and nothing else.
*
* - `cursor`: the opaque base64url keyset token from the previous page's `nextCursor`, 1 to
*   `PAGE_CURSOR_MAX` characters of `[A-Za-z0-9_-]`.
* - `limit`: an integer in [1, {@link APP_RING_VERSIONS_MAX_LIMIT}], coerced from the query
*   string. Absent means {@link APP_RING_VERSIONS_DEFAULT_LIMIT}, applied by the service.
*
* `.strict()`: an `orgId`, a sort key, an `offset` or any other key is a 400. The app scope comes
* from the caller's grants, never from the query string.
*/
const appRingVersionsQuerySchema = z.object({ ...pagedQueryFields({ maxLimit: 200 }) }).strict();
/**
* One public ring of one app, with the version pinned on it: the ring name, the version row id
* (`release.app_compositions.id`), its `appVersion`, and the build facts of that row
* ({@link appReleaseFactsSchema}). A version released before this feature carries null facts.
*/
const appRingVersionEntrySchema = z.object({
	ring: ringSchema,
	versionId: z.string().min(1),
	appVersion: z.string().min(1),
	...appReleaseFactsSchema.shape
}).strict();
/**
* One app of the page: its id, slug, name, source record (`null` when nothing was ever detected,
* D7), and its pinned rings.
*
* `rings` holds one entry per public ring that has a pin, in the order `stable`, `beta`, `alpha`,
* `edge` (`RINGS`). A ring with no pin has no entry, so an app with no pin has `rings: []`. Private
* channels never appear. The schema refuses more than four entries and a ring named twice.
*/
const appRingVersionsItemSchema = z.object({
	appId: z.string().min(1),
	slug: z.string().min(1),
	name: z.string(),
	source: appSourceSchema.nullable(),
	rings: z.array(appRingVersionEntrySchema).max(4).refine((rings) => new Set(rings.map((entry) => entry.ring)).size === rings.length, { message: "each ring appears at most once" })
}).strict();
/**
* The response of `GET /v1/apps/ring-versions`: the house keyset envelope over
* {@link appRingVersionsItemSchema}, `{ items, nextCursor, truncated }` (`pagedEnvelopeSchema`).
*
* The route has no cap concept, so `truncated` is `true` exactly when `nextCursor` is non-null:
* more apps exist after this page. Items are in the keyset order of `GET /v1/apps`: `slug`, then
* `id`. The route always answers with the envelope; it has no legacy bare-array shape.
*/
const appRingVersionsPageSchema = pagedEnvelopeSchema(appRingVersionsItemSchema);
//#endregion
export { APP_RING_VERSIONS_DEFAULT_LIMIT, APP_RING_VERSIONS_MAX_LIMIT, APP_RING_VERSIONS_PATH, appRingVersionEntrySchema, appRingVersionsItemSchema, appRingVersionsPageSchema, appRingVersionsQuerySchema };

//# sourceMappingURL=app-ring-versions.js.map